import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import KPICard from '../../components/primitives/KPICard';
import Panel from '../../components/primitives/Panel';
import StatusPill from '../../components/primitives/StatusPill';
import { PGPieChart } from '../../components/primitives/PGChart';
import {
  OPPORTUNITIES,
  PIPELINE_GROUP,
  REP_ROLLUPS,
  RENEWALS,
  STAGES,
  OPEN_STAGE_IDS,
  fmtRk,
} from '../../utils/opportunityData';
import { PG } from '../../utils/theme';

const {
  FiTrendingUp, FiZap, FiLayers, FiDollarSign,
  FiAward, FiAlertTriangle, FiCalendar, FiRefreshCw,
} = FiIcons;

const stageColor = (tone) => {
  if (tone === 'ok') return PG.emerald;
  if (tone === 'warn') return PG.amber;
  if (tone === 'info') return PG.cyan;
  return PG.neutral500;
};

const renewalTone = (status) => {
  if (status === 'overdue' || status === 'urgent') return 'alert';
  if (status === 'upcoming') return 'warn';
  return 'info';
};

const TOP_COLS = [
  { key: 'name',                   label: 'Deal',    align: 'left',  sortable: false },
  { key: 'branchName',             label: 'Branch',  align: 'left',  sortable: false },
  { key: 'customerSegmentName',    label: 'Segment', align: 'left',  sortable: false },
  { key: 'stageName',              label: 'Stage',   align: 'left',  sortable: false },
  { key: 'value',                  label: 'Value',   align: 'right', sortable: true  },
  { key: 'probability',            label: 'Prob',    align: 'right', sortable: true  },
  { key: 'daysInStage',            label: 'Days',    align: 'right', sortable: true  },
  { key: 'repName',                label: 'Rep',     align: 'left',  sortable: false },
  { key: 'expectedCloseDaysAhead', label: 'ETA',     align: 'right', sortable: true  },
];

const etaLabel = (d) => {
  if (d < 0) return `${Math.abs(d)}d ago`;
  if (d === 0) return 'today';
  return `${d}d`;
};

export default function OpportunitiesGroupView() {
  const g = PIPELINE_GROUP;

  const [sortKey, setSortKey] = useState('value');
  const [sortDir, setSortDir] = useState('desc');

  const topOpps = useMemo(() => {
    const open = OPPORTUNITIES.filter(
      (o) => OPEN_STAGE_IDS.includes(o.stageId) && o.outcome !== 'lost'
    );
    const sorted = [...open].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      return sortDir === 'asc' ? av - bv : bv - av;
    });
    return sorted.slice(0, 40);
  }, [sortKey, sortDir]);

  const toggleSort = (key) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const stalled = useMemo(
    () => OPPORTUNITIES
      .filter((o) => o.isStalled)
      .sort((a, b) => b.daysSinceActivity - a.daysSinceActivity)
      .slice(0, 12),
    []
  );
  const stalledTotal = useMemo(() => OPPORTUNITIES.filter((o) => o.isStalled).length, []);

  const sourceData = g.sourceBreakdown.map((s) => ({ name: s.name, value: s.count }));

  const renewals = RENEWALS.slice(0, 12);
  const urgentRenewals = RENEWALS.filter((r) => r.status === 'urgent' || r.status === 'overdue').length;
  const renewals90d = RENEWALS.filter((r) => r.daysToExpiry <= 90).length;

  const topReps = useMemo(
    () => [...REP_ROLLUPS].sort((a, b) => b.revenueClosedMTD - a.revenueClosedMTD).slice(0, 10),
    []
  );

  const pipelineTotal = g.pipelineValue || 1;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="h-full flex flex-col gap-4 min-h-0"
    >
      <div className="grid grid-cols-8 gap-3 flex-shrink-0">
        <KPICard
          title="Open_Pipeline"
          value={fmtRk(g.pipelineValue)}
          target={`Weighted ${fmtRk(g.weightedPipeline)}`}
          icon={FiTrendingUp}
          accent={PG.cyan}
        />
        <KPICard
          title="Weighted_Pipeline"
          value={fmtRk(g.weightedPipeline)}
          icon={FiZap}
          accent={PG.violet}
        />
        <KPICard
          title="Active_Deals"
          value={g.openCount}
          unit="deals"
          icon={FiLayers}
          accent={PG.white}
        />
        <KPICard
          title="Avg_Deal_Size"
          value={fmtRk(g.avgDealSize)}
          icon={FiDollarSign}
          accent={PG.emerald}
        />
        <KPICard
          title="Win_Rate_90d"
          value={`${g.winRate}%`}
          target="≥70%"
          icon={FiAward}
          accent={g.winRate >= 70 ? PG.emerald : PG.amber}
          trend={g.winRate >= 70 ? 'up' : 'down'}
          trendValue={g.winRate >= 70 ? 'On plan' : 'Below'}
        />
        <KPICard
          title="Stalled"
          value={g.stalledCount}
          unit=">14d"
          icon={FiAlertTriangle}
          accent={g.stalledCount > 0 ? PG.red : PG.emerald}
          trend={g.stalledCount > 0 ? 'down' : 'up'}
          trendValue={g.stalledCount > 0 ? `${g.stalledCount} idle` : 'Clean'}
        />
        <KPICard
          title="Forecast_30d"
          value={fmtRk(g.forecast30d)}
          target="weighted close next 30d"
          icon={FiCalendar}
          accent={PG.amber}
        />
        <KPICard
          title="Renewals_Due"
          value={renewals90d}
          unit="next 90d"
          icon={FiRefreshCw}
          accent={renewals90d > 0 ? PG.amber : PG.emerald}
        />
      </div>

      <div className="grid grid-cols-12 gap-3 flex-shrink-0 h-44">
        <Panel
          title="Pipeline_Funnel"
          subtitle="MTD // count and value by stage"
          right={<StatusPill tone="info">{g.openCount} open</StatusPill>}
          className="col-span-12"
        >
          <div className="h-full grid grid-cols-5 gap-2 p-3">
            {g.stageBreakdown.map((s) => {
              const c = stageColor(s.tone);
              const pct = (s.value / pipelineTotal) * 100;
              return (
                <div
                  key={s.id}
                  className="flex flex-col justify-between p-3 border border-white/10 bg-black"
                  style={{ borderColor: `${c}33` }}
                >
                  <div className="font-mono-tech text-[9px] uppercase tracking-[0.2em] text-neutral-500">
                    {s.name}
                  </div>
                  <div
                    className="font-doto text-4xl font-black leading-none"
                    style={{ color: c }}
                  >
                    {s.count}
                  </div>
                  <div>
                    <div className="font-mono-tech text-[11px] text-white">
                      {fmtRk(s.value)}
                    </div>
                    <div className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500 mt-0.5">
                      wtd {fmtRk(s.weightedValue)}
                    </div>
                    <div className="mt-2 h-1 bg-white/5">
                      <div
                        className="h-full"
                        style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: c }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Panel>
      </div>

      <div className="grid grid-cols-12 gap-3 flex-1 min-h-0">
        <Panel
          title="Top_Opportunities"
          subtitle={`${topOpps.length} open deals // click to drill`}
          right={
            <>
              <StatusPill tone="info">{g.bigTicketCount} big-ticket</StatusPill>
              <StatusPill tone={g.atRiskCount > 0 ? 'alert' : 'warn'}>{g.atRiskCount} at-risk</StatusPill>
            </>
          }
          className="col-span-8 flex-1 min-h-0"
        >
          <div className="h-full overflow-y-auto custom-scrollbar">
            <table className="w-full text-left">
              <thead className="sticky top-0 bg-[#0A0A0A] z-10">
                <tr className="border-b border-white/10">
                  {TOP_COLS.map((c) => (
                    <th
                      key={c.key}
                      onClick={() => c.sortable && toggleSort(c.key)}
                      className={`px-3 py-2 font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500 ${
                        c.align === 'right' ? 'text-right' : 'text-left'
                      } ${c.sortable ? 'cursor-pointer hover:text-white' : ''}`}
                    >
                      {c.label}
                      {c.sortable && sortKey === c.key && (
                        <span className="ml-1 text-neutral-400">
                          {sortDir === 'asc' ? '▲' : '▼'}
                        </span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {topOpps.map((o) => {
                  const tint = o.isStalled ? 'bg-[#D71921]/5 border-l-2 border-[#D71921]' : '';
                  const daysColor = o.daysInStage > 18 ? 'text-[#D71921]' : 'text-neutral-300';
                  const valColor = o.isBigTicket ? '#00C27A' : '#FFFFFF';
                  return (
                    <tr
                      key={o.id}
                      className={`row-clickable border-b border-white/5 hover:bg-white/5 transition-colors cursor-pointer ${tint}`}
                      onClick={() => { window.location.hash = `#/sliver/sales/branch/${o.branchId}`; }}
                    >
                      <td className="px-3 py-2">
                        <div className="font-mono-tech text-[11px] uppercase tracking-wider text-white truncate max-w-[280px]">
                          {o.name}
                        </div>
                        <div className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-600 truncate">
                          {o.customer}
                        </div>
                      </td>
                      <td className="px-3 py-2 font-mono-tech text-[10px] uppercase tracking-wider text-neutral-400">
                        {o.branchName}
                      </td>
                      <td className="px-3 py-2 font-mono-tech text-[10px] uppercase tracking-wider text-neutral-400">
                        {o.customerSegmentName}
                      </td>
                      <td className="px-3 py-2">
                        <StatusPill tone={o.stageTone}>{o.stageName}</StatusPill>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <span className="font-doto text-base" style={{ color: valColor }}>
                          {fmtRk(o.value)}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right font-mono-tech text-[11px] text-neutral-300">
                        {Math.round(o.probability * 100)}%
                      </td>
                      <td className={`px-3 py-2 text-right font-mono-tech text-[11px] ${daysColor}`}>
                        {o.daysInStage}d
                      </td>
                      <td className="px-3 py-2 font-mono-tech text-[10px] uppercase tracking-wider text-neutral-400 truncate max-w-[140px]">
                        {o.repName}
                      </td>
                      <td className="px-3 py-2 text-right font-mono-tech text-[11px] text-neutral-400">
                        {etaLabel(o.expectedCloseDaysAhead)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Panel>

        <div className="col-span-4 flex flex-col gap-3 min-h-0">
          <Panel
            title="Source_Attribution"
            subtitle="open deals by channel"
            className="flex-shrink-0 h-60"
          >
            <div className="h-full p-2">
              <PGPieChart data={sourceData} />
            </div>
          </Panel>

          <Panel
            title="Stalled_Watchlist"
            subtitle={`${stalledTotal} deals // no activity >14d`}
            right={<StatusPill tone={stalledTotal > 0 ? 'alert' : 'ok'}>{stalledTotal}</StatusPill>}
            className="flex-1 min-h-0"
          >
            <div className="h-full overflow-y-auto custom-scrollbar">
              <div className="p-2 space-y-1">
                {stalled.map((o) => (
                  <div
                    key={o.id}
                    onClick={() => { window.location.hash = `#/sliver/sales/branch/${o.branchId}`; }}
                    className="flex items-center justify-between px-3 py-2 border border-white/10 hover:border-[#D71921] hover:bg-[#D71921]/5 transition-all cursor-pointer"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-mono-tech text-[11px] uppercase tracking-wider text-white truncate">
                        {o.name}
                      </div>
                      <div className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-600 truncate">
                        {o.branchName} // {o.repName}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                      <div className="font-doto text-base text-white">{fmtRk(o.value)}</div>
                      <StatusPill tone="alert">{o.daysSinceActivity}d idle</StatusPill>
                    </div>
                  </div>
                ))}
                {stalled.length === 0 && (
                  <div className="p-6 text-center font-mono-tech text-[10px] uppercase tracking-widest text-neutral-500">
                    No stalled deals
                  </div>
                )}
              </div>
            </div>
          </Panel>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-3 flex-shrink-0 h-48">
        <Panel
          title="Renewals_Next_90d"
          subtitle="contracts expiring soon"
          right={<StatusPill tone={urgentRenewals > 0 ? 'alert' : 'info'}>{urgentRenewals} urgent</StatusPill>}
          className="col-span-7"
        >
          <div className="h-full overflow-y-auto custom-scrollbar">
            <div className="p-2 space-y-1">
              {renewals.map((r) => {
                const delta = r.currentValue > 0
                  ? ((r.renewalValue - r.currentValue) / r.currentValue) * 100
                  : 0;
                const deltaColor = delta >= 0 ? 'text-[#00C27A]' : 'text-[#D71921]';
                const deltaSign = delta >= 0 ? '+' : '';
                const pillLabel = r.status === 'overdue' ? 'OVERDUE' : `${r.daysToExpiry}d`;
                return (
                  <div
                    key={r.id}
                    onClick={() => { window.location.hash = `#/sliver/sales/branch/${r.branchId}`; }}
                    className="flex items-center justify-between px-3 py-2 border border-white/10 hover:border-[#D71921] hover:bg-[#D71921]/5 transition-all cursor-pointer"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-mono-tech text-[11px] uppercase tracking-wider text-white truncate">
                        {r.customer}
                      </div>
                      <div className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-600 truncate">
                        {r.branchName} // {r.ownerRep} // {r.customerSegmentName}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0 ml-3">
                      <div className="text-right">
                        <div className="font-mono-tech text-[10px] text-neutral-500">
                          {fmtRk(r.currentValue)} → <span className="text-white">{fmtRk(r.renewalValue)}</span>
                        </div>
                        <div className={`font-mono-tech text-[9px] ${deltaColor}`}>
                          {deltaSign}{delta.toFixed(1)}%
                        </div>
                      </div>
                      <StatusPill tone={renewalTone(r.status)}>{pillLabel}</StatusPill>
                    </div>
                  </div>
                );
              })}
              {renewals.length === 0 && (
                <div className="p-6 text-center font-mono-tech text-[10px] uppercase tracking-widest text-neutral-500">
                  No renewals pending
                </div>
              )}
            </div>
          </div>
        </Panel>

        <Panel
          title="Rep_Leaderboard"
          subtitle="top 10 by revenue closed MTD"
          right={<StatusPill tone="info">{REP_ROLLUPS.length} reps</StatusPill>}
          className="col-span-5"
        >
          <div className="h-full overflow-y-auto custom-scrollbar">
            <div className="p-2 space-y-1">
              {topReps.map((r, i) => (
                <div
                  key={r.id}
                  onClick={() => { window.location.hash = `#/sliver/sales/branch/${r.branchId}`; }}
                  className="flex items-center justify-between px-3 py-2 border border-white/10 hover:border-[#D71921] hover:bg-[#D71921]/5 transition-all cursor-pointer"
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="font-mono-tech text-[10px] text-neutral-500 w-6 flex-shrink-0">
                      {String(i + 1).padStart(2, '0')}
                    </div>
                    <div className="min-w-0">
                      <div className="font-mono-tech text-[11px] uppercase tracking-wider text-white truncate">
                        {r.name}
                      </div>
                      <div className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-600 truncate">
                        {r.branchName}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0 ml-2 text-right">
                    <div className="font-mono-tech text-[9px] text-neutral-500">
                      {r.wonMTD}W/{r.lostMTD}L
                    </div>
                    <div className="font-mono-tech text-[11px] text-white">
                      {fmtRk(r.revenueClosedMTD)}
                    </div>
                    <div
                      className="font-doto text-base"
                      style={{ color: r.winRate90d >= 70 ? PG.emerald : r.winRate90d >= 50 ? PG.amber : PG.red }}
                    >
                      {r.winRate90d}%
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Panel>
      </div>
    </motion.div>
  );
}
